// ** React
import { useState, MouseEvent, KeyboardEvent, useEffect } from "react"

// ** Gatsby
import { Link, navigate } from "gatsby"

// ** Icons Import
import CaretDownIcon from "../../icons/caret-down-icon"

// ** 3rd Party Libraries
import { Transition } from "@headlessui/react"
import { parseLink } from "../../utils/helpers"

type linksType = {
  id: string
  url?: string
  reference?: {
    title: string
    slug: {
      current: string
    }
  }
  title: string
}

interface NestedNavigationItemProps {
  title: string
  links: linksType[]
  setCurrent: (val: string) => void
  current: string
}

const activeStyles = {
  color: "#DD944B",
}

function NestedNavigationItem(props: NestedNavigationItemProps) {
  const { title, links, setCurrent, current } = props
  const [isOpen, setIsOpen] = useState<boolean>(false)

  useEffect(() => {
    if (title !== current) {
      setIsOpen(false)
    }
  }, [current])

  return (
    <div
      onKeyUp={() => setCurrent(title)}
      tabIndex={0}
      className="relative"
      onKeyDown={(e: KeyboardEvent<HTMLImageElement>) => {
        if (e.key === "Enter" || e.key === "Space") {
          e.preventDefault()
          setIsOpen(!isOpen)
        }
      }}
    >
      <div
        onMouseOver={(e: MouseEvent<HTMLDivElement>) => {
          e.preventDefault()
          setIsOpen(true)
        }}
        onMouseLeave={(e: MouseEvent<HTMLDivElement>) => {
          e.preventDefault()
          setIsOpen(false)
        }}
        className="text-gray-700 hover:text-blue-500 px-3 py-2 rounded-md text-base font-medium flex items-center cursor-pointer"
      >
        {title}
        <CaretDownIcon
          className={`h-4 w-4 ml-1 transform ${
            isOpen ? "rotate-180" : "rotate-0"
          } duration-300`}
          aria-hidden="true"
        />
      </div>
      <Transition
        show={isOpen}
        enter="transition ease-out duration-300 transform"
        enterFrom="opacity-0 scale-95"
        enterTo="opacity-100 scale-100"
        leave="transition ease-in duration-100 transform"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-95"
      >
        <div
          onMouseOver={(e: MouseEvent<HTMLDivElement>) => {
            e.preventDefault()
            setIsOpen(true)
          }}
          onMouseLeave={(e: MouseEvent<HTMLDivElement>) => {
            e.preventDefault()
            setIsOpen(false)
          }}
          onKeyDown={(e: KeyboardEvent<HTMLImageElement>) => {
            //Doesn't seem to work. Enter should go to the page
            if (e.key === "Escape" || e.key === "Esc") {
              e.preventDefault()
              setIsOpen(false)
            }
          }}
          className="absolute after:absolute after:content[''] after:top-0 after:transform after:-translate-y-full after:border-solid after:border-b-white after:border-b-8 after:border-x-[transparent] after:border-x-8 after:border-t-0 px-3 py-3 rounded-lg shadow-lg min-w-full left-1/2 transform -translate-x-1/2 bg-white"
        >
          <div className="grid bg-white grid-cols-[_minmax(200px,_1fr)_minmax(200px,_1fr)] gap-x-2">
            {links?.length > 0 &&
              links.map((link: linksType, idx: any) => (
                <Link
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      navigate(parseLink(link))
                    }
                  }}
                  tabIndex={0}
                  to={parseLink(link)}
                  key={idx}
                  activeStyle={activeStyles}
                  className="py-3 px-3 duration-300 hover:bg-zinc-100 rounded-md cursor-pointer text-sm bg-primary"
                >
                  <span className="block text-base font-medium">
                    {link.title}
                  </span>
                </Link>
              ))}
          </div>
        </div>
      </Transition>
    </div>
  )
}

export default NestedNavigationItem
